import { Link } from 'react-router-dom';
import { CaretDown, ChatCircleText, Gear, SignOut } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useUserProfile } from '@/hooks/useQueries';
import { useLogout } from '@/hooks/useLogout';
import { useChat } from '@/contexts/ChatContext';
import { useRutaAi } from '@/contexts/RutaAiContext';

/**
 * Account avatar + dropdown for the page-header utility cluster. Self-contained:
 * reads identity from useUserInfo()/useUserProfile() and logs out via useLogout(),
 * so it can live anywhere under the app providers without prop threading.
 */
export function HeaderProfileMenu() {
  const { fullName, role } = useUserInfo();
  const { data: userProfileData } = useUserProfile();
  const avatarUrl = userProfileData?.profile?.profilePictureUrl;
  const logout = useLogout();
  const { openAi } = useRutaAi();
  const { startNewConversation } = useChat();

  const name = fullName || 'User';
  const userRole = role || 'Admin';
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2);

  const openFeedback = () => {
    // Fresh thread so the Feedback greeting is visible; prior chats stay in history.
    startNewConversation();
    openAi(undefined, { framing: 'feedback' });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 rounded-full p-0.5 pr-1.5 text-white/90 hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          aria-label={`${name} (${userRole})`}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              className="h-7 w-7 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <span className="text-[11px] font-semibold">{initials}</span>
            </div>
          )}
          <CaretDown className="h-3.5 w-3.5 text-white/50 flex-shrink-0" weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span className="text-sm font-medium truncate">{name}</span>
          <span className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
            {userRole}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/user-settings">
            <Gear className="mr-2 h-4 w-4 flex-shrink-0" />
            <span className="whitespace-nowrap">Preferences</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            // Defer open so the dropdown can close without fighting focus.
            setTimeout(openFeedback, 0);
          }}
        >
          <ChatCircleText className="mr-2 h-4 w-4 flex-shrink-0" />
          <span className="whitespace-nowrap">Feedback</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout}>
          <SignOut className="mr-2 h-4 w-4 flex-shrink-0" />
          <span className="whitespace-nowrap">Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
